// Qual integração o Espelho oferece, e por quê.
//
// Causa raiz declarada pelo Rafa ouvindo usuários: "o VYV é complexo, tem
// muitos casos de uso e leva tempo pra conhecer todas as funcionalidades".
// Integração que ninguém descobre é integração que não existe — e os números
// confirmam: 7 conexões ativas de Google Calendar, e ZERO eventos de agenda
// vindos de integração nos últimos 7 dias contra 108 vindos do Espelho.
//
// A regra que faz isso não virar spam: a sugestão sai do USO REAL dos módulos,
// nunca de uma lista fixa. O Espelho já sabe o que a pessoa faz — se ela
// registra treino toda semana na mão, oferecer o Apple Saúde é útil; se ela
// nunca abriu o módulo de fitness, é ruído.
//
// Módulo PURO, sem imports: a decisão é testável sem banco, sem React e sem
// device. O hook (useIntegrationSuggestion) só junta os números e persiste a
// resposta.

export type SuggestableProvider =
  | "apple_health"
  | "health_connect"
  | "google_calendar"
  | "google_tasks"
  | "notion"
  | "spotify";

export type Platform = "ios" | "android" | "web";

/**
 * Contagens dos últimos 30 dias, por módulo. Todas são de dado que o USUÁRIO
 * produziu (manual, voz ou Espelho) — nunca de dado importado, senão a
 * integração passaria a justificar a si mesma.
 */
export interface ModuleUsage {
  workouts: number;
  sleepLogs: number;
  agendaEvents: number;
  tasks: number;
  notes: number;
  focusSessions: number;
}

export interface SuggestionInput {
  usage: ModuleUsage;
  /** Providers com linha em user_connections (qualquer status != revoked). */
  connectedProviders: string[];
  platform: Platform;
  /** nudge_types já dispensados de vez (3 strikes) ou em cooldown. */
  blockedNudgeTypes: string[];
}

export interface Suggestion {
  provider: SuggestableProvider;
  /** Sufixo do nudge_type gravado em user_nudges. */
  nudgeType: string;
  /** Chave i18n da frase que o Espelho diz. */
  reasonKey: string;
  /** O módulo que gerou o sinal — vira {{module}} na frase e no metadata. */
  signalModule: string;
  /** Quantas vezes a pessoa fez isso na mão nos últimos 30 dias. */
  signalCount: number;
  /** signalCount / limiar. Só serve para ordenar candidatos. */
  strength: number;
}

export function nudgeTypeFor(provider: SuggestableProvider): string {
  return "integration_suggest_" + provider;
}

// Limiares. São "a pessoa faz isso com regularidade suficiente para valer a
// pena automatizar", não "a pessoa usa o app". Deliberadamente altos: uma
// sugestão errada custa mais confiança do que uma sugestão perdida custa
// conversão, porque o Espelho é a superfície de maior intimidade do produto.
const RULES: {
  provider: SuggestableProvider;
  signalModule: keyof ModuleUsage;
  threshold: number;
  /** Se QUALQUER um destes já estiver conectado, a regra não dispara. */
  satisfiedBy: string[];
  nativeOnly?: boolean;
}[] = [
  // Saúde nativa — o hub do SO cobre Apple Watch, Oura, Fitbit, WHOOP e
  // Garmin de uma vez, então qualquer um deles conectado já resolve.
  {
    provider: "apple_health",
    signalModule: "workouts",
    threshold: 4,
    satisfiedBy: ["apple_health", "health_connect", "strava"],
    nativeOnly: true,
  },
  {
    provider: "apple_health",
    signalModule: "sleepLogs",
    threshold: 8,
    satisfiedBy: ["apple_health", "health_connect"],
    nativeOnly: true,
  },
  {
    provider: "google_calendar",
    signalModule: "agendaEvents",
    threshold: 8,
    satisfiedBy: ["google_calendar", "outlook", "apple_calendar"],
  },
  {
    provider: "google_tasks",
    signalModule: "tasks",
    threshold: 6,
    satisfiedBy: ["google_tasks"],
  },
  {
    provider: "notion",
    signalModule: "notes",
    threshold: 6,
    satisfiedBy: ["notion"],
  },
  {
    provider: "spotify",
    signalModule: "focusSessions",
    threshold: 5,
    satisfiedBy: ["spotify"],
  },
];

/**
 * No iOS o provider nativo é apple_health; no Android, health_connect. As
 * regras declaram apple_health e a plataforma decide — assim o limiar não
 * precisa ser escrito duas vezes.
 */
export function platformHealthProviderFor(platform: Platform): "apple_health" | "health_connect" | null {
  if (platform === "ios") return "apple_health";
  if (platform === "android") return "health_connect";
  return null;
}

/**
 * Devolve a melhor sugestão, ou null quando não há nenhuma honesta.
 *
 * Regras duras:
 *   - NUNCA sugere o que já está conectado (nem um equivalente que já resolve).
 *   - NUNCA sugere saúde nativa fora de iOS/Android — não existe HealthKit no
 *     navegador, e oferecer algo que não dá para aceitar é pior que silêncio.
 *   - NUNCA repete o que a pessoa dispensou de vez ou está em cooldown.
 *   - Uma por vez: vence o sinal mais forte relativo ao próprio limiar, para
 *     que "12 treinos" (3× o limiar) ganhe de "9 eventos" (1,1×).
 */
export function decideSuggestion(input: SuggestionInput): Suggestion | null {
  const connected = new Set(input.connectedProviders);
  const blocked = new Set(input.blockedNudgeTypes);
  const nativeHealth = platformHealthProviderFor(input.platform);

  let best: Suggestion | null = null;

  for (const rule of RULES) {
    if (rule.nativeOnly && nativeHealth === null) continue;

    // A regra fala em apple_health; no Android quem vale é health_connect.
    const provider: SuggestableProvider = rule.nativeOnly && nativeHealth
      ? nativeHealth
      : rule.provider;

    if (connected.has(provider)) continue;
    if (rule.satisfiedBy.some((p) => connected.has(p))) continue;

    const nudgeType = nudgeTypeFor(provider);
    if (blocked.has(nudgeType)) continue;

    const count = input.usage[rule.signalModule];
    if (typeof count !== "number" || count < rule.threshold) continue;

    const strength = count / rule.threshold;
    if (best === null || strength > best.strength) {
      best = {
        provider: provider,
        nudgeType: nudgeType,
        reasonKey: "mirror.integrationSuggest.reason." + rule.signalModule,
        signalModule: rule.signalModule,
        signalCount: count,
        strength: strength,
      };
    }
  }

  return best;
}
